/**
 * Staff screen (DNKADM-0053 U1) — the `v2_staff` entity list + create/edit/
 * deactivate/reactivate (handoff §5.6). `RequireAdmin`-gated route + a Scheduling nav
 * item ("Staff"). Replaces the v1 `lastName == "ServiceAccount"` partition with a
 * first-class staff entity (§9.1/§9.3).
 *
 * Loads `listStaff({ status })` through `useResource`, keyed on the
 * Active/Inactive/All filter (`useActiveFilter` + `<ActiveFilter>`, §4.3). A table of
 * name → email → status; the row/name/chevron click opens the detail (schedule,
 * exceptions, caps) per the DNKADM-0284 row-open pattern, with Deactivate/Reactivate
 * as explicit secondary actions that stop propagation. Base-field editing lives on
 * the detail page (DNKADM-0286 retired the list quick-edit trigger); "Add staff
 * member" opens the {@link StaffDialog} to create. Every write refetches + toasts. A
 * first-run empty tenant (§8) shows a strong empty state with a create CTA.
 *
 * **Toolbar, sort and URL (DNKADM-0430 P1-F).** The shared {@link ListToolbar} holds a
 * name/email search and the Active/Inactive/All filter; Name, Email and Status have shared
 * {@link SortableHeader}s (default Name ascending). Search, filter and sort live in the
 * URL (`staffListView.ts`).
 */
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Link as MuiLink,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { ListNoMatch } from '@/components/list/ListNoMatch';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SortableHeader } from '@/components/list/SortableHeader';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { useListUrlState } from '@/components/list/useListUrlState';
import { Icon, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useUrlActiveFilter } from '@/hooks/useActiveFilter';
import { ActiveFilter } from '@/components/ActiveFilter';
import { AssignedLocationsCell } from '@/components/AssignedLocationsCell';
import { useToast } from '@/components/useToast';
import { serverMessage } from '@/api/serverMessage';
import {
  deactivateStaff,
  listStaff,
  reactivateStaff,
  staffDisplayName,
  type V2Staff,
} from '@/api/staff';
import { getPlanUsage, planLimitFor } from '@/api/planUsage';
import { PlanLimitDialog } from '@/components/PlanLimitDialog';
import { parsePlanLimit, v2ErrorCode, type PlanLimitInfo } from '@/api/v2Errors';
import { StaffDialog } from './StaffDialog';
import {
  STAFF_DEFAULT_SORT,
  STAFF_SORT_KEYS,
  STAFF_URL_DEFAULTS,
  staffMatches,
  staffSortValue,
  type StaffSortKey,
} from './staffListView';
import { ScreenError } from '@/components/ScreenError';

export function StaffScreen() {
  const t = resolveTokens(useTheme());
  const showToast = useToast();
  const navigate = useNavigate();
  // DNKADM-0430: search, status filter and sort come from the URL (`staffListView.ts`).
  const url = useListUrlState(STAFF_URL_DEFAULTS);
  const setUrl = url.set;
  const filter = useUrlActiveFilter(url.values.status, setUrl);
  const search = url.values.q;
  const sort = parseSort(url.values.sort, url.values.dir, STAFF_SORT_KEYS, STAFF_DEFAULT_SORT);

  const staff = useResource(
    useCallback(() => listStaff({ status: filter.status }), [filter.status]),
    [filter.status],
  );

  // Plan usage drives the button-level Add gate (DNKADM-0069); loaded once and
  // refetched after each add/delete (a slot changes). Degrades to the submit-time
  // gate (DNKADM-0065) when it can't load — `planLimitFor` returns null on error.
  const planUsage = useResource(getPlanUsage);

  // Dialog state: `null` = closed; `{ staff: null }` = add; `{ staff }` = edit.
  const [dialog, setDialog] = useState<{ staff: V2Staff | null } | null>(null);
  // The plan-limit popup payload when Add is gated at the button; null = closed.
  const [planLimit, setPlanLimit] = useState<PlanLimitInfo | null>(null);

  const refetch = staff.refetch;

  const visibleRows = useMemo(
    () =>
      sortRows(
        (staff.data ?? []).filter((row) => staffMatches(row, search)),
        (row) => staffSortValue(row, sort.key),
        sort.dir,
        (row) => row.id,
      ),
    [staff.data, search, sort.key, sort.dir],
  );
  const onSort = useCallback(
    (key: StaffSortKey) => setUrl(toSortParams(toggleSort(sort, key), STAFF_DEFAULT_SORT)),
    [setUrl, sort],
  );
  const refetchUsage = planUsage.refetch;

  // Add: when the axis is at its plan cap, open the upgrade popup directly (the
  // form never opens); otherwise open the create form exactly as before. An
  // unknown/unlimited/unavailable usage never gates (`planLimitFor` → null).
  const handleAdd = useCallback(() => {
    const info = planLimitFor(planUsage.data, 'staff');
    if (info) {
      setPlanLimit(info);
      return;
    }
    setDialog({ staff: null });
  }, [planUsage.data]);

  const handleDeactivate = useCallback(
    async (member: V2Staff) => {
      try {
        await deactivateStaff(member.id);
        refetch();
        refetchUsage();
        showToast('Staff member deactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t deactivate the staff member.', 'error');
      }
    },
    [refetch, refetchUsage, showToast],
  );

  const handleReactivate = useCallback(
    async (member: V2Staff) => {
      try {
        await reactivateStaff(member.id);
        refetch();
        refetchUsage();
        showToast('Staff member reactivated', 'success');
      } catch (err) {
        // Reactivate consumes a plan slot (counts are of ACTIVE records), so it is
        // plan-limit enforced exactly like Add (DNKADM-0065 / DNKAPI-0139) — mirror
        // the create flow's branching, keyed on the body `error` code (not status).
        const code = v2ErrorCode(err);
        if (code === 'PLAN_LIMIT_REACHED') {
          // The cap was hit — open the upgrade popup (NOT a generic toast); the
          // record was not reactivated.
          setPlanLimit(parsePlanLimit(err));
          return;
        }
        if (code === 'PLAN_VERIFY_FAILED') {
          // Transient plan-service outage — a retry toast (NOT the popup).
          showToast(
            'We couldn’t verify your plan just now. Please try again in a moment.',
            'error',
          );
          return;
        }
        showToast(serverMessage(err) ?? 'Couldn’t reactivate the staff member.', 'error');
      }
    },
    [refetch, refetchUsage, showToast],
  );

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" component="h1">
            Staff
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
            The people who deliver services. Assign each to locations, then set their weekly
            schedule, date exceptions, and appointment caps.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Icon name="add" />}
          onClick={handleAdd}
          sx={{ flexShrink: 0 }}
        >
          Add staff member
        </Button>
      </Stack>

      <ListToolbar
        search={{
          value: search,
          onChange: (next) => setUrl({ q: next }),
          placeholder: 'Search staff',
          disabled: staff.status !== 'ready',
        }}
        filters={
          <ActiveFilter
            value={filter.value}
            onChange={filter.setValue}
            aria-label="Filter staff by status"
          />
        }
        onClear={() => setUrl({ q: null, status: null })}
        canClear={search.length > 0 || filter.value !== 'active'}
      />

      {staff.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={220}
          role="status"
          aria-label="Loading staff"
          sx={{ borderRadius: 3 }}
        />
      )}

      {staff.status === 'error' && (
        <ScreenError
          error={staff.error}
          what="the staff"
          onRetry={staff.refetch}
          sx={{ alignItems: 'center' }}
        />
      )}

      {staff.status === 'ready' && staff.data && staff.data.length === 0 && (
        // First-run empty state (§8) with a strong create CTA.
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: t.lineStrong,
            borderRadius: 3,
          }}
        >
          <Icon name="badge" sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography sx={{ mt: 1, fontWeight: 600 }}>
            {filter.value === 'active' ? 'No staff yet' : 'No staff match this filter'}
          </Typography>
          {filter.value === 'active' && (
            <>
              <Typography variant="body2" sx={{ mt: 0.5, mb: 2.5, color: 'text.secondary' }}>
                Add your first staff member, assign them to locations, and set their schedule to
                start offering bookable services.
              </Typography>
              <Button variant="contained" startIcon={<Icon name="add" />} onClick={handleAdd}>
                Add staff member
              </Button>
            </>
          )}
        </Paper>
      )}

      {staff.status === 'ready' && staff.data && staff.data.length > 0 && (
        <TableContainer>
          <Table aria-label="Staff" size="small">
            <TableHead>
              <TableRow>
                <SortableHeader label="Name" col="name" sort={sort} onSort={onSort} />
                <SortableHeader label="Email" col="email" sort={sort} onSort={onSort} />
                <TableCell>Locations</TableCell>
                <SortableHeader label="Status" col="status" sort={sort} onSort={onSort} />
                <TableCell align="right">Actions</TableCell>
                <TableCell sx={{ width: 44 }} aria-hidden />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((member) => {
                const name = staffDisplayName(member);
                return (
                  <TableRow
                    key={member.id}
                    hover
                    onClick={() => navigate(`/staff/${member.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <MuiLink
                        component={RouterLink}
                        to={`/staff/${member.id}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {name}
                      </MuiLink>
                    </TableCell>
                    <TableCell>{member.email ?? '—'}</TableCell>
                    <TableCell>
                      <AssignedLocationsCell locations={member.locations} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={member.isActive ? 'Active' : 'Inactive'}
                        color={member.isActive ? 'success' : 'default'}
                        variant={member.isActive ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {member.isActive ? (
                        <IconAction
                          size="small"
                          label={`Deactivate staff ${name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleDeactivate(member);
                          }}
                        >
                          <Icon name="toggle_off" />
                        </IconAction>
                      ) : (
                        <IconAction
                          size="small"
                          label={`Reactivate staff ${name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleReactivate(member);
                          }}
                        >
                          <Icon name="toggle_on" />
                        </IconAction>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ color: t.textGhost }}>
                      <IconAction
                        size="small"
                        label={`Open ${name}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/staff/${member.id}`);
                        }}
                      >
                        <Icon name="chevron_right" />
                      </IconAction>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {staff.status === 'ready' &&
        staff.data &&
        staff.data.length > 0 &&
        visibleRows.length === 0 && <ListNoMatch noun="staff" query={search} />}

      <StaffDialog
        open={dialog !== null}
        staff={dialog?.staff ?? null}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          refetch();
          refetchUsage();
        }}
        showToast={showToast}
      />

      <PlanLimitDialog
        open={planLimit !== null}
        info={planLimit}
        onClose={() => setPlanLimit(null)}
      />
    </Box>
  );
}
